import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import type { RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import type {
  TrustpilotCollectorState,
  TrustpilotCollectorViewModel,
} from '@/features/jobs/details/ratings/TrustpilotCollectorViewModel'
import styles from './SubmitRatingsDialog.module.css'
import logoUrl from '@/assets/misc/img_taxfyle-symbol.svg'
import { FormContainer } from '@/components/FormContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { Avatar } from '@/components/Avatar'
import { type ReactNode, useId } from 'react'
import { StarRating } from '@/features/jobs/details/ratings/components/StarRating'
import { Id } from '@/infra/id'
import { Spinner } from '@/components/Spinner'
import { TrustpilotCollector } from '@/features/jobs/details/ratings/components/TrustpilotCollector'
import { getPlatformRatingPresentation } from '@/features/jobs/details/ratings/rating-presentation'

interface JobRatingsDialogProps {
  vm: RatingsViewModel
}

interface PlatformRatingHeaderProps {
  isTaxJob?: boolean
}

export function PlatformRatingHeader({
  isTaxJob = true,
}: PlatformRatingHeaderProps) {
  return (
    <ModalDialogHeader>
      <RatingModalLogo />
      <ModalDialogTitle>
        {isTaxJob
          ? 'How do you rate the quality of your return?'
          : 'How do you rate the quality of our service offering?'}
      </ModalDialogTitle>
      <ModalDialogDescription>
        We appreciate your review.
      </ModalDialogDescription>
    </ModalDialogHeader>
  )
}

interface ProRatingHeaderProps {
  avatarUrl?: string | null
  avatarDisplayName?: string
}

export function ProRatingHeader({
  avatarUrl,
  avatarDisplayName,
}: ProRatingHeaderProps) {
  return (
    <ModalDialogHeader>
      <div className="flex items-center justify-center flex-col mb-md">
        <Avatar
          subject={{ avatarUrl, displayName: avatarDisplayName }}
          size={64}
        />
      </div>
      <ModalDialogTitle>How was the Pro's service?</ModalDialogTitle>
      <ModalDialogDescription>
        Our Pros appreciate your review.
      </ModalDialogDescription>
    </ModalDialogHeader>
  )
}

interface RatingContentProps {
  onRatingChange: (rating: number) => unknown
  rating: number
  children: ReactNode
  testId?: string
}

export function RatingBody({
  rating,
  onRatingChange,
  children,
  testId,
}: RatingContentProps) {
  return (
    <>
      <ModalDialogContent spacing="compact">
        <StarRating value={rating} onChange={onRatingChange} testId={testId} />

        {children}
      </ModalDialogContent>
    </>
  )
}

interface RatingsDialogProps {
  isOpen: boolean
  isRatingValid: boolean
  onSubmit: () => unknown
  onClose: () => unknown
  children: ReactNode
  isPending: boolean
  testId?: string
  submitButtonTestId?: string
  notNowButtonTestId?: string
}

export function RatingDialog({
  isOpen,
  onClose,
  onSubmit,
  isRatingValid,
  isPending,
  children,
  testId,
  submitButtonTestId,
  notNowButtonTestId,
}: RatingsDialogProps) {
  const formId = useId()

  return (
    <FormContainer id={formId} onSubmit={onSubmit}>
      <ModalDialog
        isOpen={isOpen}
        onClose={onClose}
        isUsingDialogScrolling
        testId={testId}
      >
        {children}

        <ModalDialogFooter>
          <ModalDialogActions>
            <Button
              isDisabled={!isRatingValid}
              isPending={isPending}
              type="submit"
              form={formId}
              label="Submit review"
              variant="default"
              testId={submitButtonTestId}
            />
            <Button
              label="Not now"
              variant="outline"
              onPress={onClose}
              isDisabled={isPending}
              testId={notNowButtonTestId}
            />
          </ModalDialogActions>
        </ModalDialogFooter>
      </ModalDialog>
    </FormContainer>
  )
}

interface RatingDialogProps {
  vm: RatingsViewModel
}

interface TrustpilotReviewDialogContainerProps {
  vm: TrustpilotCollectorViewModel
  reference: string
}

export const ProRatingDialogContainer = observer(
  function ProRatingDialogContainer({ vm }: RatingDialogProps) {
    const testId = 'pro-rating-dialog'

    return (
      <RatingDialog
        isOpen={vm.proRatingModal.showing}
        onClose={vm.close}
        onSubmit={vm.submitProRating}
        isRatingValid={vm.isRatingValid}
        isPending={vm.submitProRating.pending}
        testId={testId}
        submitButtonTestId={`${testId}-submit-button`}
        notNowButtonTestId={`${testId}-not-now-button`}
      >
        {vm.pro && (
          <ProRatingHeader
            avatarUrl={vm.pro.avatarUrl}
            avatarDisplayName={vm.pro.displayName}
          />
        )}

        <RatingBody
          rating={vm.rating}
          onRatingChange={vm.setRating}
          testId={`${testId}-star-rating`}
        >
          <TextAreaContainer
            rows={4}
            model={vm}
            field="feedback"
            label="Add a written review"
            name="feedback"
            placeholder="Tell us more about your experience."
            hint="The feedback is optional."
            testId={`${testId}-feedback`}
          />
        </RatingBody>
      </RatingDialog>
    )
  },
)

export const PlatformRatingDialogContainer = observer(
  function PlatformRatingDialogContainer({ vm }: RatingDialogProps) {
    const testId = 'platform-rating-dialog'

    return (
      <RatingDialog
        onClose={vm.close}
        onSubmit={vm.submitPlatformRating}
        isRatingValid={vm.isRatingValid}
        isOpen={vm.platformRatingModal.showing}
        isPending={vm.submitPlatformRating.pending}
        testId={testId}
        submitButtonTestId={`${testId}-submit-button`}
        notNowButtonTestId={`${testId}-not-now-button`}
      >
        <PlatformRatingHeader isTaxJob={vm.isTaxJob} />

        <RatingBody
          rating={vm.rating}
          onRatingChange={vm.setRating}
          testId={`${testId}-star-rating`}
        >
          {vm.platformRatingPresentation.feedbackPrompt && (
            <TextAreaContainer
              rows={4}
              model={vm}
              field="feedback"
              label={vm.platformRatingPresentation.feedbackPrompt}
              name="feedback"
              placeholder="Tell us more about your experience."
              hint="This feedback is optional."
              testId={`${testId}-feedback`}
            />
          )}
        </RatingBody>
      </RatingDialog>
    )
  },
)

interface TrustpilotReviewDialogProps {
  isOpen: boolean
  rating: number | null
  trustpilotState: TrustpilotCollectorState
  onClose: () => unknown
  children?: ReactNode
}

export function TrustpilotReviewDialog({
  isOpen,
  rating,
  trustpilotState,
  onClose,
  children,
}: TrustpilotReviewDialogProps) {
  const testId = 'trustpilot-review-dialog'
  const presentation = getPlatformRatingPresentation(
    rating ?? 0,
    true,
  ).trustpilot

  if (!presentation) {
    return null
  }

  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <RatingModalLogo />
        <ModalDialogTitle>{presentation.title}</ModalDialogTitle>
        <ModalDialogDescription>
          {presentation.description}
        </ModalDialogDescription>
      </ModalDialogHeader>

      <ModalDialogContent>
        {trustpilotState === 'loading' && (
          <div
            className={styles.trustpilotStatus}
            role="status"
            data-test={`${testId}-loading`}
          >
            <Spinner />
            <span>Loading Trustpilot…</span>
          </div>
        )}

        {trustpilotState === 'error' && (
          <div
            className={styles.trustpilotStatus}
            role="alert"
            data-test={`${testId}-error`}
          >
            <p>Trustpilot could not be loaded.</p>
          </div>
        )}

        {children}
      </ModalDialogContent>

      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Not now"
            variant={presentation.notNowButtonVariant}
            onPress={onClose}
            testId={`${testId}-not-now-button`}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

export const TrustpilotReviewDialogContainer = observer(
  function TrustpilotReviewDialogContainer({
    vm,
    reference,
  }: TrustpilotReviewDialogContainerProps) {
    return (
      <TrustpilotReviewDialog
        isOpen={vm.modal.showing}
        rating={vm.submittedRating}
        trustpilotState={vm.trustpilotState}
        onClose={vm.close}
      >
        {vm.modal.showing && (
          <TrustpilotCollector
            vm={vm}
            reference={reference}
            testId="trustpilot-review-dialog-collector"
          />
        )}
      </TrustpilotReviewDialog>
    )
  },
)

export const JobRatingsDialogsContainer = observer(
  function JobRatingsDialogsContainer({ vm }: JobRatingsDialogProps) {
    return (
      <>
        <ProRatingDialogContainer vm={vm} />
        <PlatformRatingDialogContainer vm={vm} />
        <TrustpilotReviewDialogContainer
          vm={vm.trustpilotCollectorVm}
          reference={Id.assert(vm.job.id)}
        />
      </>
    )
  },
)

function RatingModalLogo() {
  return <img className={styles.logo} src={logoUrl} alt="Taxfyle logo" />
}
